import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { sb } from '../../supabase';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { formatCatalogPriceSource } from '../../lib/displayCurrency';
import { CATEGORY_TREE } from '../../lib/categoryTree';

// Catalog QA: pick a factory, fix each product's main image (swap in one of its own
// gallery photos when the import picked a size-chart), or hide junk products from the
// buyer catalog. Writes go through the admin-only admin_set_product_display RPC.
export default function ConsoleCatalogQA({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const [factories, setFactories] = useState([]);
  const [fid, setFid] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      const { data } = await sb.from('factory_directory_public')
        .select('id, company_name, company_name_latin')
        .order('company_name_latin', { ascending: true });
      setFactories(data || []);
    })();
  }, []);

  const loadProducts = useCallback(async (factoryId) => {
    if (!factoryId) { setProducts([]); return; }
    setLoading(true); setErr('');
    const { data, error } = await sb.from('factory_products')
      .select('id, name_en, name_ar, name_zh, image, gallery_images, is_hidden, subcategory, price, price_amount, price_max, price_currency')
      .eq('factory_id', factoryId)
      .order('sort_order', { ascending: true, nullsFirst: false });
    if (error) setErr(error.message || 'load failed');
    setProducts(data || []);
    setLoading(false);
  }, []);
  useEffect(() => { loadProducts(fid); }, [fid, loadProducts]);

  const setMain = async (p, url) => {
    if (url === p.image || busyId) return;
    setBusyId(p.id); setErr('');
    const { error } = await sb.rpc('admin_set_product_display', { p_product_id: p.id, p_image_url: url });
    if (error) setErr(error.message || 'update failed');
    else setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, image: url } : x)));
    setBusyId(null);
  };
  const toggleHide = async (p) => {
    if (busyId) return;
    setBusyId(p.id); setErr('');
    const { error } = await sb.rpc('admin_set_product_display', { p_product_id: p.id, p_is_hidden: !p.is_hidden });
    if (error) setErr(error.message || 'update failed');
    else setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, is_hidden: !x.is_hidden } : x)));
    setBusyId(null);
  };

  // Factory Latin-name edit (company_name_latin).
  const [nameEdit, setNameEdit] = useState('');
  const [savingName, setSavingName] = useState(false);
  useEffect(() => {
    const f = factories.find((x) => x.id === fid);
    setNameEdit(f ? (f.company_name_latin || f.company_name || '') : '');
  }, [fid, factories]);
  const saveFactoryName = async () => {
    const nm = nameEdit.trim();
    if (!fid || nm.length < 2 || savingName) return;
    setSavingName(true); setErr('');
    const { error } = await sb.rpc('admin_set_factory_name', { p_factory_id: fid, p_name_latin: nm });
    if (error) setErr(error.message || 'update failed');
    else setFactories((prev) => prev.map((f) => (f.id === fid ? { ...f, company_name_latin: nm } : f)));
    setSavingName(false);
  };
  const setSubcat = async (p, code) => {
    if (busyId) return;
    setBusyId(p.id); setErr('');
    const { error } = await sb.rpc('admin_set_product_taxonomy', { p_product_id: p.id, p_subcategory: code || null });
    if (error) setErr(error.message || 'update failed');
    else setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, subcategory: code || null } : x)));
    setBusyId(null);
  };

  const pName = (p) => (isAr ? (p.name_ar || p.name_en) : lang === 'zh' ? (p.name_zh || p.name_en) : (p.name_en || p.name_ar)) || '—';
  const imgList = (p) => Array.from(new Set([p.image, ...(Array.isArray(p.gallery_images) ? p.gallery_images : [])].filter(Boolean)));
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? products.filter((p) => pName(p).toLowerCase().includes(t)) : products;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, q, isAr, lang]);

  const control = { border: '1px solid var(--ac-line)', background: 'var(--ac-card,#fff)', color: 'var(--ac-ink)', borderRadius: 10, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', minHeight: 40 };

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="catalog-qa">
      <div style={{ padding: '20px clamp(14px,4vw,30px)', maxWidth: 1180, margin: '0 auto', fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--ac-ink)' }}>{isAr ? 'مراجعة الكتالوج' : 'Catalog QA'}</h1>
        <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--ac-muted)' }}>
          {isAr ? 'اختر مصنعًا، ثم اضغط أي صورة من المعرض لتصير الرئيسية، أو أخفِ منتجًا من الكتالوج.'
                : 'Pick a factory, tap any gallery image to make it the main one, or hide a product from the catalog.'}
        </p>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '16px 0' }}>
          <select value={fid} onChange={(e) => setFid(e.target.value)} style={{ ...control, minWidth: 240, flex: '1 1 240px' }}>
            <option value="">{isAr ? '— اختر مصنع —' : '— pick a factory —'}</option>
            {factories.map((f) => <option key={f.id} value={f.id}>{f.company_name_latin || f.company_name}</option>)}
          </select>
          {fid && (
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={isAr ? 'بحث في المنتجات…' : 'search products…'}
                   style={{ ...control, minWidth: 180, flex: '1 1 180px' }} />
          )}
        </div>

        {fid && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '0 0 12px', flexDirection: isAr ? 'row-reverse' : 'row' }}>
            <span style={{ fontSize: 12, color: 'var(--ac-muted)', whiteSpace: 'nowrap' }}>{isAr ? 'اسم المصنع:' : 'Factory name:'}</span>
            <input value={nameEdit} onChange={(e) => setNameEdit(e.target.value)} dir="ltr" style={{ ...control, minWidth: 220, flex: '1 1 220px' }} />
            <button onClick={saveFactoryName} disabled={savingName || nameEdit.trim().length < 2} style={{ border: '1px solid var(--ac-line)', background: '#1A1814', color: '#fff', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: savingName ? 0.6 : 1 }}>
              {savingName ? (isAr ? 'يحفظ…' : 'Saving…') : (isAr ? 'حفظ الاسم' : 'Save name')}
            </button>
          </div>
        )}
        {err && <p style={{ color: '#c0503f', fontSize: 13 }}>{err === 'admin only' ? (isAr ? 'أدمن فقط.' : 'Admin only.') : err}</p>}
        {fid && <p style={{ fontSize: 12, color: 'var(--ac-muted)', margin: '0 0 10px' }}>{loading ? (isAr ? 'يحمّل…' : 'Loading…') : `${shown.length} ${isAr ? 'منتج' : 'products'}`}</p>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))', gap: 14 }}>
          {shown.map((p) => {
            const list = imgList(p);
            const busy = busyId === p.id;
            return (
              <div key={p.id} style={{ border: '1px solid var(--ac-line)', borderRadius: 14, background: 'var(--ac-card,#fff)', padding: 12, opacity: p.is_hidden ? 0.5 : 1, position: 'relative' }}>
                {p.is_hidden && <span style={{ position: 'absolute', top: 8, insetInlineEnd: 8, fontSize: 10, fontWeight: 700, color: '#c0503f', background: 'rgba(192,80,63,0.1)', border: '1px solid rgba(192,80,63,0.3)', borderRadius: 99, padding: '2px 8px' }}>{isAr ? 'مخفي' : 'hidden'}</span>}
                <div style={{ aspectRatio: '1 / 1', borderRadius: 10, overflow: 'hidden', background: '#f0ebe3', marginBottom: 8 }}>
                  {p.image && <img src={p.image} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ac-ink)', lineHeight: 1.4, height: '2.8em', overflow: 'hidden' }}>{pName(p)}</div>
                <div style={{ fontSize: 12, color: 'var(--ac-muted)', margin: '3px 0 6px' }}>{formatCatalogPriceSource(p) || (isAr ? 'عند الطلب' : 'On request')}</div>

                <select value={p.subcategory || ''} onChange={(e) => setSubcat(p, e.target.value)} disabled={busy}
                        style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--ac-line)', borderRadius: 8, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit', color: 'var(--ac-ink)', margin: '0 0 8px', direction: isAr ? 'rtl' : 'ltr', background: p.subcategory ? 'var(--ac-card,#fff)' : 'rgba(192,80,63,0.06)' }}>
                  <option value="">{isAr ? '— بلا فئة —' : '— no category —'}</option>
                  {CATEGORY_TREE.map((cat) => (
                    <optgroup key={cat.code} label={isAr ? cat.ar : cat.en}>
                      {cat.sections.flatMap((s) => s.subs).map((sub) => (
                        <option key={sub.code} value={sub.code}>{isAr ? sub.ar : sub.en}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>

                <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
                  {list.map((url) => (
                    <button key={url} onClick={() => setMain(p, url)} disabled={busy}
                            title={url === p.image ? (isAr ? 'الرئيسية' : 'main') : (isAr ? 'اجعلها الرئيسية' : 'set as main')}
                            style={{ flexShrink: 0, width: 50, height: 50, borderRadius: 8, overflow: 'hidden', padding: 0, cursor: busy ? 'default' : 'pointer',
                              border: url === p.image ? '2px solid #2D6A4F' : '1px solid var(--ac-line)', background: '#f0ebe3', outline: 'none' }}>
                      <img src={url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    </button>
                  ))}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, flexDirection: isAr ? 'row-reverse' : 'row' }}>
                  <span style={{ fontSize: 11, color: 'var(--ac-muted)' }}>{list.length} {isAr ? 'صور' : 'imgs'}</span>
                  <button onClick={() => toggleHide(p)} disabled={busy}
                          style={{ fontSize: 11.5, fontWeight: 700, padding: '5px 12px', borderRadius: 8, cursor: busy ? 'default' : 'pointer', border: '1px solid',
                            ...(p.is_hidden
                              ? { background: 'rgba(45,106,79,0.10)', color: '#2D6A4F', borderColor: 'rgba(45,106,79,0.3)' }
                              : { background: 'rgba(192,80,63,0.07)', color: '#c0503f', borderColor: 'rgba(192,80,63,0.26)' }) }}>
                    {p.is_hidden ? (isAr ? 'إظهار' : 'Unhide') : (isAr ? 'إخفاء' : 'Hide')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {fid && !loading && shown.length === 0 && (
          <div style={{ padding: 28, textAlign: 'center', color: 'var(--ac-muted)' }}>{isAr ? 'لا منتجات.' : 'No products.'}</div>
        )}
        {!fid && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ac-faint,#9C978D)' }}>{isAr ? 'اختر مصنعًا للبدء.' : 'Pick a factory to start.'}</div>
        )}
      </div>
    </ConsoleShell>
  );
}
